import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import {
  LayoutDashboard,
  Settings,
  LogOut,
  Menu,
  X,
  PackageX,
  Bell,
  Ban,
  ShieldAlert,
  ClipboardList,
  DatabaseZap,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import clsx from 'clsx';
import api, { DashboardSummary } from '@/lib/api';

const overviewItems = [
  { to: '/', icon: LayoutDashboard, label: 'Dashboard', end: true },
  { to: '/lagerrisiken', icon: ShieldAlert, label: 'Lagerrisiken' },
  { to: '/bestelllisten', icon: ClipboardList, label: 'Bestelllisten' },
  { to: '/slow-movers', icon: PackageX, label: 'Überbestand' },
];

const configItems = [
  { to: '/alerts', icon: Bell, label: 'Benachrichtigungen' },
  { to: '/stammdaten', icon: DatabaseZap, label: 'Stammdaten' },
  { to: '/excluded-products', icon: Ban, label: 'Ausgeschlossene' },
  { to: '/settings', icon: Settings, label: 'Einstellungen' },
];

function initials(name: string | undefined): string {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  return parts
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('');
}

function relativeSync(dateStr: string | null): string {
  if (!dateStr) return 'noch kein Sync';
  const diffMin = Math.floor((Date.now() - new Date(dateStr).getTime()) / 60000);
  if (diffMin < 2) return 'gerade eben';
  if (diffMin < 60) return `vor ${diffMin} Min.`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `vor ${diffH} Std.`;
  return new Date(dateStr).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
}

function SidebarLink({
  to,
  icon: Icon,
  label,
  end,
  badge,
  onClick,
}: {
  to: string;
  icon: typeof LayoutDashboard;
  label: string;
  end?: boolean;
  badge?: number;
  onClick: () => void;
}) {
  return (
    <NavLink
      to={to}
      end={end}
      onClick={onClick}
      className={({ isActive }) =>
        clsx(
          'flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-sm transition-colors',
          isActive
            ? 'bg-brand-soft text-brand-deep font-bold'
            : 'text-ink-muted font-semibold hover:bg-surface-2 hover:text-ink'
        )
      }
    >
      <Icon className="h-[18px] w-[18px] flex-shrink-0" />
      <span className="flex-1">{label}</span>
      {badge != null && badge > 0 && (
        <span className="font-mono text-[11px] font-medium text-white bg-risk-red rounded-full px-2 py-px">
          {badge}
        </span>
      )}
    </NavLink>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [summary, setSummary] = useState<DashboardSummary | null>(null);

  useEffect(() => {
    api
      .getDashboardSummary()
      .then(setSummary)
      .catch(() => setSummary(null));
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const closeSidebar = () => setSidebarOpen(false);
  const riskBadge = summary ? summary.red + summary.yellow : 0;

  return (
    <div className="h-screen flex bg-cream">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/40 lg:hidden"
          onClick={closeSidebar}
        />
      )}

      {/* Sidebar */}
      <aside
        className={clsx(
          'fixed inset-y-0 left-0 z-50 w-64 bg-surface border-r border-line flex flex-col transform transition-transform duration-200 lg:relative lg:translate-x-0',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <div className="flex items-center justify-between px-5 pt-6 pb-5">
          <div className="flex items-center gap-2.5">
            <img src="/logo-cube.svg" alt="" className="h-7 w-7" />
            <span className="text-[15px] font-extrabold tracking-tight text-ink">
              always<span className="text-brand">·</span>on
              <span className="text-brand">·</span>stock
            </span>
          </div>
          <button onClick={closeSidebar} className="lg:hidden text-ink-soft hover:text-ink">
            <X className="h-5 w-5" />
          </button>
        </div>

        <nav className="flex-1 px-3 space-y-1 overflow-y-auto">
          <div className="section-label px-3.5 pb-1.5">Übersicht</div>
          {overviewItems.map((item) => (
            <SidebarLink
              key={item.to}
              {...item}
              badge={item.to === '/lagerrisiken' ? riskBadge : undefined}
              onClick={closeSidebar}
            />
          ))}
          <div className="section-label px-3.5 pt-4 pb-1.5">Konfiguration</div>
          {configItems.map((item) => (
            <SidebarLink key={item.to} {...item} onClick={closeSidebar} />
          ))}
        </nav>

        <div className="p-4 space-y-3">
          {summary && (
            <div className="bg-brand-soft rounded-panel px-4 py-3">
              <div className="flex items-center gap-2 mb-1">
                <span
                  className={clsx(
                    'w-[7px] h-[7px] rounded-full',
                    summary.syncStatus === 'error' ? 'bg-risk-red' : 'bg-brand'
                  )}
                />
                <span className="font-mono text-[11px] font-medium text-brand-deep">
                  {summary.syncStatus === 'error' ? 'Sync-Fehler' : 'weclapp verbunden'}
                </span>
              </div>
              <div className="font-mono text-[10.5px] text-ink-muted">
                Sync: {relativeSync(summary.lastSyncAt)} · {summary.totalProducts} Artikel
              </div>
            </div>
          )}
          <div className="flex items-center gap-3 px-1">
            <div className="w-9 h-9 rounded-full bg-risk-peach flex items-center justify-center text-white font-bold text-[13px] flex-shrink-0">
              {initials(user?.name)}
            </div>
            <div className="leading-tight min-w-0 flex-1">
              <div className="text-[13px] font-bold text-ink truncate">{user?.name}</div>
              <div className="font-mono text-[10.5px] text-ink-soft truncate">
                {user?.tenantName ?? user?.email}
              </div>
            </div>
            <button
              onClick={handleLogout}
              title="Abmelden"
              className="text-ink-soft hover:text-ink transition-colors flex-shrink-0"
            >
              <LogOut className="h-[18px] w-[18px]" />
            </button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar (mobile) */}
        <header className="lg:hidden flex items-center h-14 px-4 bg-surface border-b border-line">
          <button onClick={() => setSidebarOpen(true)} className="text-ink-muted hover:text-ink">
            <Menu className="h-6 w-6" />
          </button>
          <div className="ml-3 flex items-center gap-2">
            <img src="/logo-cube.svg" alt="" className="h-6 w-6" />
            <span className="text-sm font-extrabold tracking-tight text-ink">
              always<span className="text-brand">·</span>on
              <span className="text-brand">·</span>stock
            </span>
          </div>
        </header>

        <main className="flex-1 overflow-auto p-4 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
